import { http } from '../../../shared/api/http';
import { Chat, Message, RecipientType, OrderStatus, MessagesPaginationResult } from '../types';
import { logger } from '../../../shared/utils/logger';

export class ChatService {
  
  /**
   * Загрузка списка чатов с пагинацией (batch запрос)
   */
  async loadChats(page = 1, limit = 500): Promise<{ chats: Chat[], hasMore: boolean }> {
    try {
      logger.debug(`📋 Загружаем чаты: страница ${page}, лимит ${limit}`);
      
      const response = await http.get('/chats', {
        params: { page, limit }
      });

      let chats = (response.data.chats || []).map((chat: any) => this.normalizeChat(chat));
      
      // Сортируем чаты: менеджерский чат всегда первый, остальные по времени
      chats = this.sortChatsWithManagerFirst(chats);
      
      // 🎉 НАЗВАНИЯ УЖЕ ПРАВИЛЬНЫЕ! Бэкенд возвращает их из БД
      // Асинхронное обновление больше не нужно
      
      logger.debug(`✅ Загружено ${chats.length} чатов`);
      
      return {
        chats,
        hasMore: response.data.hasMore || false
      };
    } catch (error) {
      logger.error('❌ Ошибка загрузки чатов:', error);
      throw error;
    }
  }

  /**
   * Загрузка сообщений чата с пагинацией
   */
  async loadChatMessages(
    chatId: string, 
    page = 1, 
    limit = 50
  ): Promise<MessagesPaginationResult> {
    try {
      logger.debug(`💬 Загружаем сообщения чата ${chatId}: страница ${page}, лимит ${limit}`);
      
      // Определяем тип чата и соответствующий endpoint
      let endpoint: string;
      
      if (chatId === 'telegram_general') {
        // Общий чат с менеджерами
        endpoint = '/chats/telegram_general/messages';
      } else if (chatId.startsWith('chat_') || !isNaN(Number(chatId))) {
        // Чат заказа
        // 🔥 ИСПРАВЛЕНИЕ: правильно извлекаем orderId
        const orderId = chatId.startsWith('chat_') ? chatId.replace('chat_', '') : chatId;
        endpoint = `/orders/${orderId}/chat`;
      } else {
        throw new Error(`Неизвестный тип чата: ${chatId}`);
      }

      // Добавляем параметры пагинации для чатов заказов
      const params = (chatId !== 'telegram_general') ? { page, limit } : {};
      const response = await http.get(endpoint, { params });
      
      const rawMessages = response.data.messages || [];
      const total = response.data.total || 0;
      const hasMore = response.data.has_more || false;
      
      logger.debug(`📥 Сырые данные сообщений с бэкенда:`, rawMessages.slice(0, 3)); // Показываем первые 3 для отладки
      
      const messages = rawMessages.map((msg: any) => {
        const normalized = this.normalizeMessage(msg);
        logger.debug(`🔄 Нормализация сообщения:`, {
          original: { author: msg.author, is_own: msg.is_own, recipient: msg.recipient },
          normalized: { author: normalized.author, is_own: normalized.is_own, recipient: normalized.recipient }
        });
        return normalized;
      });
      
      logger.debug(`✅ Загружено ${messages.length} сообщений для чата ${chatId} (всего: ${total}, есть еще: ${hasMore})`);
      
      return {
        messages,
        hasMore,
        total
      };
    } catch (error) {
      logger.error(`❌ Ошибка загрузки сообщений чата ${chatId}:`, error);
      throw error;
    }
  }

  /**
   * Отправка текстового сообщения
   */
  async sendMessage(
    chatId: string, 
    content: string, 
    recipient: RecipientType
  ): Promise<Message> {
    try {
      logger.debug(`📤 Отправляем сообщение в чат ${chatId} для ${recipient}:`, content);
      
      // Определяем endpoint в зависимости от типа чата
      let endpoint: string;
      let payload: any;
      
      if (chatId === 'telegram_general') {
        // Общий чат с менеджерами
        endpoint = '/chats/telegram_general/messages';
        payload = { message: content };
      } else if (chatId.startsWith('chat_') || !isNaN(Number(chatId))) {
        // Чат заказа
        // 🔥 ИСПРАВЛЕНИЕ: правильно извлекаем orderId
        const orderId = chatId.startsWith('chat_') ? chatId.replace('chat_', '') : chatId;
        endpoint = `/orders/${orderId}/chat`;
        payload = { 
          message: content, 
          recipient
        };
      } else {
        throw new Error(`Неизвестный тип чата: ${chatId}`);
      }

      const response = await http.post(endpoint, payload);

      // Создаем временное сообщение для отображения
      const message: Message = {
        id: `temp_${Date.now()}`,
        content,
        author: 'Вы',
        is_own: true,
        created_at: new Date().toISOString(),
        status: 'sent',
        recipient,
        files: [],
        message_type: 'text'
      };
      
      logger.debug(`✅ Сообщение отправлено:`, message);
      
      return message;
    } catch (error) {
      logger.error(`❌ Ошибка отправки сообщения в чат ${chatId}:`, error);
      throw error;
    }
  }

  /**
   * Отправка файлового сообщения
   */
  async sendFileMessage(
    chatId: string,
    files: File[],
    recipient: RecipientType,
    messageContent: string
  ): Promise<Message> {
    try {
      logger.debug(`📎 Отправляем ${files.length} файлов в чат ${chatId} для ${recipient}`);
      
      const formData = new FormData();
      
      // Определяем endpoint в зависимости от типа чата
      let endpoint: string;
      
      if (chatId === 'telegram_general') {
        // Общий чат - пока не поддерживает файлы
        throw new Error('Отправка файлов в общий чат пока не поддерживается');
      } else if (chatId.startsWith('chat_') || !isNaN(Number(chatId))) {
        // Чат заказа
        // 🔥 ИСПРАВЛЕНИЕ: правильно извлекаем orderId
        const orderId = chatId.startsWith('chat_') ? chatId.replace('chat_', '') : chatId;
        
        if (files.length === 1) {
          // Один файл
          endpoint = `/orders/${orderId}/chat/upload`;
          formData.append('file', files[0]);
        } else {
          // Несколько файлов
          endpoint = `/orders/${orderId}/chat/upload-multiple`;
          files.forEach(file => {
            formData.append('files', file);
          });
        }
        
        formData.append('recipient', recipient);
        formData.append('message', messageContent);
      } else {
        throw new Error(`Неизвестный тип чата: ${chatId}`);
      }

      const response = await http.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Создаем временное сообщение для отображения
      const message: Message = {
        id: `temp_${Date.now()}`,
        content: messageContent, // Используем текст сообщения, а не имена файлов
        author: 'Вы',
        is_own: true,
        created_at: new Date().toISOString(),
        status: 'sent',
        recipient,
        files: files.map(f => ({
          id: `temp_${Date.now()}_${Math.random()}`,
          name: f.name,
          size: f.size,
          type: f.type,
          // Сначала временный blob-URL; после перезагрузки будет заменен реальной ссылкой из API
          url: URL.createObjectURL(f)
        })),
        message_type: 'file'
      };
      
      logger.debug(`✅ Файлы отправлены:`, message);
      
      return message;
    } catch (error) {
      logger.error(`❌ Ошибка отправки файлов в чат ${chatId}:`, error);
      throw error;
    }
  }

  /**
   * Поиск существующего чата для заказа в уже загруженном списке
   * НЕ делает дополнительных запросов - использует текущий список чатов
   */
  async findOrderChat(orderId: string): Promise<Chat | null> {
    try {
      logger.debug(`🔍 Ищем существующий чат для заказа ${orderId} в текущем списке`);
      
      // ❌ УБИРАЕМ дублирующий запрос loadChats() - используем уже загруженные данные
      // Если чат не найден в текущем списке, значит его действительно нет
      // Список чатов уже загружен в handleShow() или при инициализации
      
      logger.warn(`⚠️ findOrderChat больше не делает запросы - чат должен быть найден в уже загруженном списке`);
      return null;
      
    } catch (error) {
      logger.error(`❌ Ошибка поиска чата для заказа ${orderId}:`, error);
      throw error;
    }
  }

  /**
   * Отметка сообщений как прочитанные
   */
  async markChatAsRead(chatId: string): Promise<void> {
    try {
      logger.debug(`👁️ Отмечаем чат ${chatId} как прочитанный`);
      
      // Определяем endpoint в зависимости от типа чата
      let endpoint: string;
      
      if (chatId === 'telegram_general') {
        // Общий чат с менеджерами
        endpoint = '/chats/telegram_general/mark-read';
      } else if (chatId.startsWith('chat_') || !isNaN(Number(chatId))) {
        // Чат заказа
        // 🔥 ИСПРАВЛЕНИЕ: правильно извлекаем orderId
        const orderId = chatId.startsWith('chat_') ? chatId.replace('chat_', '') : chatId;
        endpoint = `/orders/${orderId}/chat/mark-read`;
      } else {
        throw new Error(`Неизвестный тип чата: ${chatId}`);
      }
      
      await http.post(endpoint);
      
      logger.debug(`✅ Чат ${chatId} отмечен как прочитанный`);
    } catch (error) {
      logger.error(`❌ Ошибка отметки чата ${chatId} как прочитанного:`, error);
      throw error;
    }
  }

  /**
   * Получение информации о статусе заказа
   */
  getOrderStatusInfo(status: OrderStatus) {
    const statusMap = {
      'new': {
        icon: '🆕',
        title: 'Новый заказ',
        subtitle: 'Ожидает обработки',
        bgColor: '#eff6ff',
        borderColor: '#bfdbfe',
        textColor: '#1e40af',
        subtitleColor: '#3b82f6'
      },
      'in_review': {
        icon: '👀',
        title: 'На просмотре',
        subtitle: 'Изучаем требования',
        bgColor: '#fef3c7',
        borderColor: '#fcd34d',
        textColor: '#92400e',
        subtitleColor: '#d97706'
      },
      'calculating': {
        icon: '🧮',
        title: 'Расчет стоимости',
        subtitle: 'Подготавливаем предложение',
        bgColor: '#e0f2fe',
        borderColor: '#7dd3fc',
        textColor: '#0c4a6e',
        subtitleColor: '#0284c7'
      },
      'waiting_confirmation': {
        icon: '⏳',
        title: 'Ожидает подтверждения',
        subtitle: 'Ждем вашего решения',
        bgColor: '#fef3c7',
        borderColor: '#fcd34d',
        textColor: '#92400e',
        subtitleColor: '#d97706'
      },
      'confirmed': {
        icon: '✅',
        title: 'Подтвержден',
        subtitle: 'Заказ принят в работу',
        bgColor: '#dcfce7',
        borderColor: '#a7f3d0',
        textColor: '#166534',
        subtitleColor: '#047857'
      },
      'in_production': {
        icon: '🏭',
        title: 'В производстве',
        subtitle: 'Изготавливаем заказ',
        bgColor: '#e0f2fe',
        borderColor: '#7dd3fc',
        textColor: '#0c4a6e',
        subtitleColor: '#0284c7'
      },
      'completed': {
        icon: '🎉',
        title: 'Выполнен',
        subtitle: 'Заказ готов',
        bgColor: '#dcfce7',
        borderColor: '#a7f3d0',
        textColor: '#166534',
        subtitleColor: '#047857'
      },
      'rejected': {
        icon: '❌',
        title: 'Отклонен',
        subtitle: 'Заказ не принят',
        bgColor: '#fef2f2',
        borderColor: '#fecaca',
        textColor: '#991b1b',
        subtitleColor: '#dc2626'
      },
      'cancelled': {
        icon: '🚫',
        title: 'Отменен',
        subtitle: 'Заказ отменен',
        bgColor: '#f3f4f6',
        borderColor: '#d1d5db',
        textColor: '#4b5563',
        subtitleColor: '#6b7280'
      }
    };

    return statusMap[status] || statusMap['new'];
  }

  // Методы для асинхронного обновления названий удалены - они больше не нужны!
  // Бэкенд теперь возвращает правильные названия сразу из БД

  /**
   * Сортировка чатов с приоритетом менеджерского чата
   */
  sortChatsWithManagerFirst(chats: Chat[]): Chat[] {
    return chats.sort((a, b) => {
      // Менеджерский чат всегда первый
      if (a.id === 'telegram_general' && b.id !== 'telegram_general') return -1;
      if (b.id === 'telegram_general' && a.id !== 'telegram_general') return 1;
      
      // Остальные чаты сортируем по времени последнего сообщения (новые первые)
      const timeA = new Date(a.sortTime || a.lastMessageTime).getTime();
      const timeB = new Date(b.sortTime || b.lastMessageTime).getTime();
      
      return timeB - timeA;
    });
  }

  // Методы для получения названий заказов удалены - они больше не нужны!
  // Бэкенд возвращает правильные названия сразу из таблицы groups

  /**
   * Нормализация данных чата
   */
  private normalizeChat(chat: any): Chat {
    return {
      id: chat.id || chat.chat_id || `chat_${chat.order_id}`,
      order_id: chat.order_id,
      offer_id: chat.offer_id,
      title: chat.title || `Заказ #${chat.order_id}`, // Уже правильное название из БД!
      lastMessage: chat.lastMessage || chat.last_message || 'Нет сообщений',
      lastMessageTime: chat.lastMessageTime || chat.last_message_time || new Date().toISOString(),
      unreadCount: chat.unreadCount || chat.unread_count || 0,
      status: chat.status || 'new',
      avatar: chat.avatar,
      sortTime: chat.sortTime || chat.sort_time || chat.lastMessageTime || new Date().toISOString(),
      groupId: chat.groupId || chat.group_id,
      price_formatted: chat.price_formatted,
      delivery_time_formatted: chat.delivery_time_formatted,
      chatType: chat.chatType || chat.chat_type || 'order',
      isPinned: chat.isPinned || chat.is_pinned || false
    };
  }

  /**
   * Нормализация данных сообщения
   */
  private normalizeMessage(message: any): Message {
    // Определяем is_own на основе автора сообщения
    let isOwn = message.is_own || message.isOwn || false;
    
    // Дополнительная логика определения собственных сообщений
    const author = message.author || message.sender || 'Unknown';
    
    // Если автор "Вы" или содержит признаки клиентского сообщения
    if (author === 'Вы' || author === 'You' || author === 'Client') {
      isOwn = true;
    }
    
    // Если есть recipient (получатель), значит это сообщение от клиента к менеджеру/технологу
    if (message.recipient && (message.recipient === 'manager' || message.recipient === 'technologist')) {
      isOwn = true;
    }
    
    // Проверяем по типу отправителя (если есть поле sender_type)
    if (message.sender_type === 'client' || message.sender_type === 'user') {
      isOwn = true;
    }
    
    // Проверяем по ID пользователя (если есть user_id и он совпадает с текущим)
    // Это можно будет добавить позже, когда будет доступна информация о текущем пользователе
    
    return {
      id: message.id || message.message_id || `msg_${Date.now()}`,
      content: message.content || message.message || '',
      author: author,
      is_own: isOwn,
      created_at: message.created_at || message.createdAt || new Date().toISOString(),
      status: message.status || 'sent',
      recipient: message.recipient,
      files: message.files || [],
      message_type: message.message_type || message.messageType || 'text',
      // Системные сообщения
      is_system: message.is_system || false,
      is_rejection: message.is_rejection || false,
      is_calc_cancelled: message.is_calc_cancelled || false,
      is_completion_notification: message.is_completion_notification || false,
      is_defect_notification: message.is_defect_notification || false,
      is_contractor_price: message.is_contractor_price || false,
      is_order_defect: message.is_order_defect || false,
      is_contractor_accepted: message.is_contractor_accepted || false,
      is_status_change: message.is_status_change || false,
      is_deadline_notification: message.is_deadline_notification || false,
      is_manager_rejection: message.is_manager_rejection || false,
      is_revision_request: message.is_revision_request || false
    };
  }
}

// Экспортируем singleton экземпляр
export const chatService = new ChatService();
